import { Injectable } from '@angular/core';
import { 
    CustomerReservationList, 
    CustomerOffer,
    ReservationClientStatus,
    ReservationAdminStatus,
    ReservationActionState,
    OfferStatus
} from '../_models/reservation.model';

@Injectable({
    providedIn: 'root'
})
export class ReservationActionsUtilsService {
    
    constructor() { }

    /**
     * Determine which actions are available for a reservation
     */
    getReservationActionState(reservation: CustomerReservationList): ReservationActionState {
        const state: ReservationActionState = {
            canAcceptOffer: this.canAcceptOffer(reservation),
            canRejectOffer: this.canRejectOffer(reservation),
            canRequestNewOffer: this.canRequestNewOffer(reservation),
            canUpdateReservation: this.canUpdateReservation(reservation),
            canCancelReservation: this.canCancelReservation(reservation),
            canDeleteReservation: this.canDeleteReservation(reservation),
            canGiveFeedback: this.canGiveFeedback(reservation),
            canSeeFeedbackDetails: this.canSeeFeedbackDetails(reservation),
            showSingleOfferPrice: this.shouldShowSingleOfferPrice(reservation),
            singleOfferPrice: this.getSingleOfferPrice(reservation)
        };

        return state;
    }

    /**
     * Check if customer can accept an offer
     */
    private canAcceptOffer(reservation: CustomerReservationList): boolean {
        // Check if reservation has offers
        if (!reservation.offers || reservation.offers.length === 0) {
            return false;
        }

        // Check reservation status
        if (reservation.reservationClientStatus === ReservationClientStatus.ACCEPTED) {
            return false;
        }

        if (reservation.reservationAdminStatus === ReservationAdminStatus.CANCELLED ||
            reservation.reservationAdminStatus === ReservationAdminStatus.REJECTED_BY_ADMIN) {
            return false;
        }

        // Check if there's at least one pending offer
        const hasPendingOffer = reservation.offers.some(offer => 
            offer.status === OfferStatus.PENDING
        );

        return hasPendingOffer;
    }

    /**
     * Check if customer can reject an offer
     */
    private canRejectOffer(reservation: CustomerReservationList): boolean {
        // Check if reservation has offers
        if (!reservation.offers || reservation.offers.length === 0) {
            return false;
        }

        // Check if there's at least one pending offer
        const hasPendingOffer = reservation.offers.some(offer => 
            offer.status === OfferStatus.PENDING
        );

        return hasPendingOffer;
    }

    /**
     * Check if customer can request a new offer
     */
    private canRequestNewOffer(reservation: CustomerReservationList): boolean {
        // Can request new offer if:
        // 1. Reservation is in OFFER_CREATED or CONFIRMED status
        // 2. There are no pending offers
        // 3. Reservation is not cancelled or rejected       

        /*if (reservation.reservationAdminStatus !== ReservationAdminStatus.OFFER_CREATED &&
            reservation.reservationAdminStatus !== ReservationAdminStatus.CONFIRMED &&
            reservation.reservationClientStatus !== ReservationClientStatus.REJECTED_BY_CLIENT) {
            return false;
        }*/

        if (reservation.reservationClientStatus === ReservationClientStatus.ACCEPTED) {
            //&& reservation.reservationAdminStatus === ReservationAdminStatus.CONFIRMED &&
            return false;
        }

        if (reservation.reservationAdminStatus === ReservationAdminStatus.CANCELLED ||
            reservation.reservationAdminStatus === ReservationAdminStatus.REJECTED_BY_ADMIN) {
            return false;
        }

        if (reservation.reservationClientStatus === ReservationClientStatus.REQUESTED_NEW_OFFER) {
            return false;
        }

        /*if (reservation.reservationAdminStatus === ReservationAdminStatus.CANCELLED ||
            reservation.reservationAdminStatus === ReservationAdminStatus.REJECTED_BY_ADMIN) {
            return false;
        }*/


        // Check if there are any pending offers
        /*const hasPendingOffer = reservation.offers?.some(offer => 
            offer.status === OfferStatus.PENDING
        ) || false;*/

        return true;
    }

    /**
     * Check if customer can update reservation
     */
    private canUpdateReservation(reservation: CustomerReservationList): boolean {
        // Can update if:
        // 1. No offers yet
        // 2. Status is WAITING_OFFER
        // 3. Not accepted by client

        if (reservation.reservationClientStatus === ReservationClientStatus.ACCEPTED) {
            return false;
        }

        if (reservation.reservationAdminStatus !== ReservationAdminStatus.WAITING_OFFER) {
            return false;
        }

        // Check if there are any offers
        const hasOffers = reservation.offers && reservation.offers.length > 0;
        
        return !hasOffers;
    }

    /**
     * Check if customer can cancel reservation
     */
    private canCancelReservation(reservation: CustomerReservationList): boolean {
        // Can cancel if:
        // 1. Not accepted by client
        // 2. Not already cancelled or rejected
        
        if (reservation.reservationClientStatus === ReservationClientStatus.ACCEPTED) {
            return false;
        }

        if (reservation.reservationAdminStatus === ReservationAdminStatus.CANCELLED ||
            reservation.reservationAdminStatus === ReservationAdminStatus.REJECTED_BY_ADMIN) {
            return false;
        }

        return true;
    }

    /**
     * Check if customer can delete reservation
     */
    private canDeleteReservation(reservation: CustomerReservationList): boolean {
        // Can delete if:
        // 1. Not confirmed by admin
        // 2. Not accepted by client
        
        if (reservation.reservationAdminStatus === ReservationAdminStatus.CONFIRMED) {
            return false;
        }

        if (reservation.reservationClientStatus === ReservationClientStatus.ACCEPTED) {
            return false;
        }

        return true;
    }

    private canGiveFeedback(reservation: CustomerReservationList): boolean {        
        // Check if reservation is completed (ended and confirmed)
              
        // Reservation must be ended
        const [datePart, timePart] = reservation.endDateTime.split(' ');
        const [day, month, year] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);

        const endDateTime = new Date(year, month - 1, day, hours, minutes);
        const now = new Date();
        now.setHours(now.getHours() + 1);

        if (endDateTime.getTime() > now.getTime()) {
           return false;
        } 
        
        // Reservation must be confirmed and accepted
        if (reservation.reservationAdminStatus !== ReservationAdminStatus.CONFIRMED &&
            reservation.reservationClientStatus !== ReservationClientStatus.ACCEPTED) {
            return false;
        }        
        return true;
    }

    private canSeeFeedbackDetails(reservation: CustomerReservationList): boolean {                
        // Reservation must be confirmed and accepted
        if (reservation.reservationAdminStatus !== ReservationAdminStatus.CONFIRMED &&
            reservation.reservationClientStatus !== ReservationClientStatus.ACCEPTED) {
            return false;
        }       
        if(!reservation.serviceFeedbackId){
            return false;
        }
        return true;
    }

    /**
     * Check if should show single offer price
     */
    private shouldShowSingleOfferPrice(reservation: CustomerReservationList): boolean {
        if (!reservation.offers || reservation.offers.length !== 1) {
            return false;
        }

        const offer = reservation.offers[0];
        return offer.status === OfferStatus.PENDING || offer.status === OfferStatus.ACCEPTED;
    }

    /**
     * Get formatted single offer price
     */
    private getSingleOfferPrice(reservation: CustomerReservationList): string | undefined {
        if (!this.shouldShowSingleOfferPrice(reservation) || !reservation.offers || reservation.offers.length !== 1) {
            return undefined;
        }

        const offer = reservation.offers[0];
        return new Intl.NumberFormat('fr-BE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2
        }).format(offer.price);
    }

    /**
     * Get the first pending offer (for accept/reject actions)
     */
    getFirstPendingOffer(reservation: CustomerReservationList): CustomerOffer | null {
        if (!reservation.offers || reservation.offers.length === 0) {
            return null;
        }

        const pendingOffer = reservation.offers.find(offer => 
            offer.status === OfferStatus.PENDING
        );

        return pendingOffer || null;
    }

    /**
     * Get French translation for action button
     */
    getActionButtonText(action: string): string {
        switch (action) {
            case 'accept': return 'Accepter';
            case 'reject': return 'Refuser';
            case 'request_new': return 'Demander nouvelle offre';
            case 'update': return 'Modifier';
            case 'cancel': return 'Annuler';
            case 'delete': return 'Supprimer';
            case 'see_feedback': return 'Voir mon avis';
            default: return action;
        }
    }

    /**
     * Get CSS class for action button
     */
    getActionButtonClass(action: string): string {
        switch (action) {
            case 'accept': return 'accept-btn';
            case 'reject': return 'reject-btn';
            case 'request_new': return 'request-new-btn';
            case 'update': return 'update-btn';
            case 'cancel': return 'cancel-btn';
            case 'delete': return 'delete-btn';
            case 'see_feedback': return 'request-new-btn';
            default: return '';
        }
    }

    /**
     * Get icon for action button
     */
    getActionButtonIcon(action: string): string {
        switch (action) {
            case 'accept': return 'check-circle';
            case 'reject': return 'x-circle';
            case 'request_new': return 'refresh-cw';
            case 'update': return 'edit';
            case 'cancel': return 'x';
            case 'delete': return 'trash';
            default: return '';
        }
    }
}